import { Skeleton } from '@chakra-ui/react';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { routes } from '@blockpulse3/data/shared';
import {
  FundraisingStatus,
  useGetFundraisingQuery,
  useUpdateFundraisingMutation,
} from '@blockpulse3/graphql/hooks';

import { getCrowdfundingFundraisingParameters } from '../../../utils';
import { ICrowdfundingFundraisingParametersForm } from '../types';
import { NewCrowdfundingFundraisingInformations } from './NewCrowdfundingFundraisingInformations';

type Props = unknown;

/**
 * NewCrowdfundingFundraisingParamters.
 * First step of fundraising funnel, parameters.
 *
 * @returns {JSX.Element}
 */
export function NewCrowdfundingFundraisingParameters(): JSX.Element {
  const i18nFundraisingParametersForm = useTranslations('FundraisingParametersForm');

  const { fundraisingId = '', companyId = '' } = useParams();
  const navigate = useNavigate();

  const [updateFundraising, { loading: updateLoading }] = useUpdateFundraisingMutation();
  const { data, loading, refetch } = useGetFundraisingQuery({
    variables: { fundraisingId },
    skip: !fundraisingId,
    onCompleted: (data) => {
      if (data.fundraising.status !== FundraisingStatus.DRAFT) {
        navigate(routes.workspace.fmt(companyId));
      }
    },
    onError: () => {
      navigate(routes.workspace.fmt(companyId));
    },
  });

  const defaultValues = useMemo(() => {
    return getCrowdfundingFundraisingParameters(data?.fundraising);
  }, [data]);

  const handleFormSubmit = (data: ICrowdfundingFundraisingParametersForm): void => {
    const selectedHoldingMethods = data.allowedHoldingMethods.map(
      (holdingMethod) => holdingMethod.value,
    );

    const crowdfundingFundrainsingVariables = {
      fundraisingId,
      name: data.name,
      pricePerShare: data.pricePerShare,
      subscriptionPeriod: data.subscriptionPeriod.value,
      assetType: data.assetType,
      allowedHoldingMethods: selectedHoldingMethods,
      hardCap: data.hardCap,
      subscriptionWorkflowType: data.subscriptionWorkflowType,
      minimalAmount: data.minimalAmount,
      maximalAmount: data.maximalAmount,
      hasMifid: data.hasMifid,
      softCap: data.softCap,
    };

    updateFundraising({
      variables: {
        updateFundraisingInput: crowdfundingFundrainsingVariables,
      },
      onCompleted: () => {
        if (companyId) {
          navigate(
            routes.workspace.fundraising.crowdfunding.documentation.fmt(companyId, fundraisingId),
          );
          refetch();
        }
      },
    });
  };

  return (
    <Skeleton isLoaded={!loading} w="full">
      <NewCrowdfundingFundraisingInformations
        defaultValues={defaultValues}
        isLoading={updateLoading}
        loadingText={i18nFundraisingParametersForm('UpdateLoading')}
        onSubmit={handleFormSubmit}
      />
    </Skeleton>
  );
}

export type NewCrowdfundingFundraisingParametersProps = Props;
